<template>
  <div v-if="survey && summary && summary.totalAnswered > 0"
       class="survey-response-summary">
    <div class="summary-item">
      <h2>
        {{ $t('userResearch.survey.response.byPeriod') }}
      </h2>
      <div class="item">
        <div class="counts">
          <div class="value">
            <div class="number">{{ summary.totalExposed | thousand }}</div>
            <div class="label">{{ $t('userResearch.survey.response.exposures') }}</div>
          </div>
          <div class="value">
            <div class="number red">{{ summary.totalAnswered | thousand }}</div>
            <div class="label">{{ $t('userResearch.survey.response.responses') }}</div>
          </div>
          <div class="value">
            <div class="number yellow">{{ summary.totalAnswered / summary.totalExposed * 100 | fixed(0) }}%</div>
            <div class="label">{{ $t('userResearch.survey.response.responseRate') }}</div>
          </div>
        </div>
        <app-chart v-if="periodChartOption"
                   chartType="barLine"
                   class="period-chart"
                   :chartOptions="periodChartOption"
                   :width="910"
                   :height="250" />
      </div>
    </div>

    <div v-if="!loading"
         class="summary-item">
      <h2>
        {{ $t('userResearch.survey.response.pageRanking.title') }}
        <span>
        {{ $t('userResearch.survey.response.pageRanking.description') }}
        </span>
      </h2>
      <div class="item">
        <table>
          <thead>
            <tr>
              <th>{{ $t('userResearch.survey.response.pageRanking.rank') }}</th>
              <th>{{ $t('userResearch.survey.response.pageRanking.pageTitle') }}</th>
              <th>{{ $t('userResearch.survey.response.pageRanking.url') }}</th>
              <th class="response">{{ $t('userResearch.survey.response.pageRanking.response') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, rank) in computedPageRankMap"
                :key="`rank-${rank}`">
              <td class="ranking">
                {{ rank + 1 }}
              </td>
              <td>
                {{ item.title }}
              </td>
              <td>
                {{ item.url }}
              </td>
              <td class="response">
                {{ item.completeCount }}
                ({{ item.completeCount / (summary.totalAnswered) * 100 | fixed(1) }}%)
              </td>
            </tr>
            <tr v-if="!isPdf && hasOtherPage > 0">
              <td class="ranking">
                6
              </td>
              <td>
                {{ $t('userResearch.survey.response.other') }}
              </td>
              <td class="other">
                <button @click="toggleOtherPages">
                  <i class="icon icon--new-window"></i>
                </button>
              </td>
              <td class="response">
                {{ hasOtherPage }}
                ({{ hasOtherPage / (summary.totalAnswered) * 100 | fixed(1) }}%)
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="!loading"
         class="summary-item questions">
      <h2>
        {{ $t('userResearch.survey.response.byQuestion.title') }}
      </h2>

      <div v-for="(question, index) in questionList"
           :key="`question-${index}`"
           class="item question">
        <div class="index">
          No. {{ index + 1 }}
        </div>
        <h4>
          {{ question.question }}
        </h4>
        <p v-if="question.questionType === SURVEY_CONSTANT.ITEM_TYPE_CHECKBOX"
           class="description">
          {{ $t('userResearch.survey.detail.multiple') }}
        </p>
        <p v-if="!question.require"
           class="description">
          {{ $t('common.optional') }}
        </p>
        <div class="count">
          {{ $t('userResearch.survey.response.response') }}
          <span class="number">
            {{ isPdf && question.questionType === SURVEY_CONSTANT.ITEM_TYPE_TEXT ? Math.min(100, question.totalAnswered) : question.totalAnswered | thousand }}
          </span>
            {{ $t('userResearch.survey.response.responseUnit') }}
        </div>
        <div v-if="question.questionType !== SURVEY_CONSTANT.ITEM_TYPE_TEXT"
             class="question-chart">
          <div class="chart-type">
            <ba-button v-if="question.questionType === SURVEY_CONSTANT.ITEM_TYPE_RADIO"
                       :icon="'icon--chart-pie-circle'"
                       :iconActive="'icon--chart-pie-circle white'"
                       :keepPressed="pagination[index] === 'pie'"
                       @click="changePagination(index, 'pie')"></ba-button>
            <ba-button :icon="'icon--chart-bar-vertical'"
                       :iconActive="'icon--chart-bar-vertical white'"
                       :keepPressed="pagination[index] === 'vertical'"
                       @click="changePagination(index, 'vertical')"></ba-button>
            <ba-button :icon="'icon--chart-bar-horizontal'"
                       :iconActive="'icon--chart-bar-horizontal white'"
                       :keepPressed="pagination[index] === 'horizontal'"
                       @click="changePagination(index, 'horizontal')"></ba-button>
          </div>
          <div class="chart-area">
            <app-chart chartType="bar"
                       :chartOptions="chartOption[index]"
                       :width="910"
                       :height="400" />
            <div v-if="question.questionType === 'range' && (question.answerOptions.minText || question.answerOptions.maxText)"
                 :class="['label', pagination[index]]">
              <div class="min">
                {{ question.answerOptions.minText }}
              </div>
              <div class="max">
                {{ question.answerOptions.maxText }}
              </div>
            </div>
            <div v-if="pagination[index] === 'pie'"
                 class="legend">
              <ul>
                <li v-for="(item, idx) in chartData[index]"
                    :key="`legend-${index}-${idx}`">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 16 16">
                    <circle :fill="pieColor[idx]" cx="6" cy="6" r="6"/>
                  </svg>
                  {{ question.answerOptions[item[0]] || $t('userResearch.survey.create.other') }}
                </li>
              </ul>
            </div>
          </div>
          <div class="chart-data">
            <table :class="question.questionType">
              <thead>
                <tr>
                  <th v-if="question.questionType !== 'range'">{{ $t('userResearch.survey.response.byQuestion.rank') }}</th>
                  <th v-else>{{ $t('userResearch.survey.response.byQuestion.range') }}</th>
                  <th v-if="question.questionType !== 'range'">{{ $t('userResearch.survey.response.byQuestion.name') }}</th>
                  <th v-else></th>
                  <th>{{ $t('userResearch.survey.response.byQuestion.response') }}</th>
                  <th class="response">{{ $t('userResearch.survey.response.byQuestion.ratio') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, rank) in chartData[index]"
                    :key="`rank2-${rank}`">
                  <td v-if="question.questionType !== 'range'"
                      class="ranking">
                    {{ rank + 1 }}
                  </td>
                  <td v-else
                      class="ranking">
                    {{ rank + question.answerOptions.minValue }}
                  </td>
                  <td v-if="question.questionType !== 'range'">
                    <template v-if="item[0] === 'Z'">
                      {{ $t('common.etc2') }}
                      <button v-if="!isPdf"
                              class="more"
                              @click="toggleOtherOptions(question)">
                        <i class="icon icon--new-window"></i>
                      </button>
                    </template>
                    <template v-else>
                      {{ question.answerOptions[item[0]] || $t('userResearch.survey.create.other') }}
                    </template>
                  </td>
                  <td v-else
                      class="range">
                    <template v-if="question.answerOptions.minText && rank === 0">
                      ({{ question.answerOptions.minText }})
                    </template>
                    <template v-else-if="question.answerOptions.maxText && rank + question.answerOptions.minValue === question.answerOptions.maxValue">
                      ({{ question.answerOptions.maxText }})
                    </template>
                  </td>
                  <td class="count">
                    {{ item[1] | thousand }}
                  </td>
                  <td class="response">
                    {{ item[1] / question.totalAnswered * 100 | fixed(0) }}%
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <template v-if="question.questionType !== 'range'">
                    <td></td>
                    <td v-if="question.questionType === SURVEY_CONSTANT.ITEM_TYPE_RADIO">
                      {{ $t('userResearch.survey.response.byQuestion.singleResult') }}
                    </td>
                    <td v-else>
                      {{ $t('userResearch.survey.response.byQuestion.multipleResult') }}
                    </td>
                  </template>
                  <template v-else>
                    <td colspan="2"
                        class="range">
                      {{ $t('userResearch.survey.response.byQuestion.singleResult') }}
                    </td>
                  </template>
                  <td>
                    {{ chartData[index].reduce((prev, curr) => prev + curr[1], 0) }}
                  </td>
                  <td class="response">
                    100%
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
        <div v-else
             class="question-answers">
          <div class="navigation">
            <ui-navigation :currentPage="pagination[index]"
                           :lastPage="Math.ceil(question.totalAnswered / 10)"
                           @setPage="changePagination(index, $event)" />
          </div>
          <ul :class="{ pdf: isPdf }">
            <li v-for="(item, idx) in chartData[index]"
                 :key="`ans-${index}-${idx}`">
              {{ item || '&nbsp;' }}
            </li>
          </ul>
          <div class="navigation bottom">
            <ui-navigation :currentPage="pagination[index]"
                           :lastPage="Math.ceil(question.totalAnswered / 10)"
                           @setPage="changePagination(index, $event)" />
          </div>
        </div>
      </div>
    </div>

    <dimmed v-if="otherPage">
      <div class="other-pages">
        <h5>
          {{ $t('userResearch.survey.response.otherPages.title') }}
        </h5>
        <i class="icon icon--close-size16"
           @click="toggleOtherPages"></i>

        <div class="thead">
          <table>
            <thead>
              <tr>
                <th>{{ $t('userResearch.survey.response.otherPages.pageUrl') }}</th>
                <th class="count">{{ $t('userResearch.survey.response.otherPages.responseCount') }}</th>
                <th class="rate">{{ $t('userResearch.survey.response.otherPages.rate') }}</th>
              </tr>
            </thead>
          </table>
        </div>
        <div class="tbody">
          <table>
            <tbody>
              <template v-for="page in otherPage">
                <tr v-if="parseInt(page.gUrlId)"
                    :key="`page-${page.gUrlId}`">
                  <td>
                    <a :href="page.url"
                       target="_blank">
                      <i class="icon icon--ba-link"></i>
                      {{ page.url }}
                    </a>
                  </td>
                  <td class="count">
                    {{ page.completeCount | thousand }}
                  </td>
                  <td class="rate">
                    <span class="bar">
                      <span :style="{ width: `${page.completeCount / (summary.totalAnswered) * 100}%` }"></span>
                    </span>
                    {{ page.completeCount / (summary.totalAnswered) * 100 | fixed(1) }}%
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </dimmed>

    <dimmed v-if="otherOptions">
      <div class="other-pages">
        <h5>
          {{ $t('userResearch.survey.response.otherOptions.title') }}
        </h5>
        <i class="icon icon--close-size16"
           @click="toggleOtherOptions"></i>

        <div class="thead">
          <table>
            <thead>
            <tr>
              <th class="rank">{{ $t('userResearch.survey.response.byQuestion.rank') }}</th>
              <th class="name">{{ $t('userResearch.survey.response.byQuestion.name') }}</th>
              <th class="count">{{ $t('userResearch.survey.response.byQuestion.response') }}</th>
              <th class="rate">{{ $t('userResearch.survey.response.byQuestion.ratio') }}</th>
            </tr>
            </thead>
          </table>
        </div>
        <div class="tbody">
          <table>
            <tbody>
              <template v-for="option in otherOptions">
                <tr :key="`option-${option.rank}`">
                  <td class="rank">
                    {{ option.rank + 1 | thousand }}
                  </td>
                  <td class="name">
                    {{ option.option }}
                  </td>
                  <td class="count">
                    {{ option.response | thousand }}
                  </td>
                  <td class="rate">
                      <span class="bar">
                        <span :style="{ width: `${option.rate}%` }"></span>
                      </span>
                    {{ option.rate | fixed(1) }}%
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </dimmed>
  </div>
</template>

<script>
import { format, parse } from 'date-fns'
import { getTextAnswer, getUrlEtcRank } from '@/api/survey'
import { SURVEY_CONSTANT, SURVEY_MAX_TEXT_ANSWER_COUNT } from '@/libs/constants'

import AppChart from '@/components/chart'
import BaButton from '@/components/ui/ba/Button'
import UiNavigation from '@/components/ui/UiNavigation'
import Dimmed from '@/components/ui/Dimmed'

export default {
  name: 'SurveyResponseSummary',
  components: {
    AppChart,
    BaButton,
    UiNavigation,
    Dimmed
  },
  created () {
    this.makeData()
  },
  methods: {
    makeData () {
      this.loading = true
      if (!this.summary || !this.summary.statsPerQuestion) {
        return false
      }

      this.questionList = this.survey.questions
      this.questionList.sort((a, b) => {
        return a.questionOrder > b.questionOrder ? 1 : -1
      })

      this.questionList = this.questionList.filter(item => {
        return !item.deleted
      })

      this.chartData = []
      this.pagination = []
      this.chartOption = []
      const questions = {}
      for (const question of this.survey.questions) {
        questions[question.questionId] = question
      }

      for (let i in this.questionList) {
        const question = this.questionList[i]
        const key = question.questionId

        const summary = this.summary.statsPerQuestion[key]

        let data = []

        question.answers = summary ? summary.answers : []
        question.totalAnswered = summary ? summary.totalAnswered : 0
        question.totalSession = summary ? summary.totalSession : 0

        if (question.questionType !== SURVEY_CONSTANT.ITEM_TYPE_TEXT) {
          if (question.questionType === 'range') {
            let rawData = [...Array(question.answerOptions.maxValue - question.answerOptions.minValue + 1).keys()].map(x => String(question.answerOptions.minValue + x++))
            for (const a of rawData) {
              data.push([a, question.answers[a] || 0])
            }
          } else {
            let dataLength = 0
            if (question.answerOptions) {
              let rawData = Object.entries(question.answerOptions)
              if (question.answerOther) {
                rawData.push(['None', this.$t('userResearch.survey.create.other')])
              }
              rawData.sort((a, b) => {
                const aValue = question.answers[a[0]] || 0
                const bValue = question.answers[b[0]] || 0
                return bValue - aValue
              })
              for (const a of rawData) {
                if (dataLength > 10) {
                  data[10] = ['Z', data[10][1] + (question.answers[a[0]] || 0)]
                } else {
                  data.push([a[0], question.answers[a[0]] || 0])
                }
                dataLength++
              }
            }
          }
          if (question.questionType === SURVEY_CONSTANT.ITEM_TYPE_RADIO) {
            this.pagination.push('pie')
          } else {
            this.pagination.push('vertical')
          }
        } else {
          data = [...question.answers]
          this.pagination.push(1)
        }

        if (question.questionType !== SURVEY_CONSTANT.ITEM_TYPE_TEXT) {
          if (question.questionType === SURVEY_CONSTANT.ITEM_TYPE_RADIO) {
            this.chartOption.push(this.pieChartOption(data, question))
          } else {
            this.chartOption.push(this.verticalChartOption(data, question))
          }
        } else {
          this.chartOption.push(null)
        }

        this.chartData.push(data)
      }

      // for (const q of Object.entries(this.summary.statsPerQuestion)) {
      //   const key = q[0]
      //   const question = q[1]
      //   let data = []
      //   if (question.questionType !== SURVEY_CONSTANT.ITEM_TYPE_TEXT) {
      //     if (question.questionType === 'range') {
      //       let rawData = [...Array(question.answerOptions.maxValue - question.answerOptions.minValue + 1).keys()].map(x => String(question.answerOptions.minValue + x++))
      //       for (const a of rawData) {
      //         data.push([a, question.answers[a] || 0])
      //       }
      //     } else {
      //       let dataLength = 0
      //       if (questions[key]) {
      //         let rawData = Object.entries(questions[key].answerOptions)
      //         if (questions[key].answerOther) {
      //           rawData.push(['None', this.$t('userResearch.survey.create.other')])
      //         }
      //         rawData.sort((a, b) => {
      //           const aValue = question.answers[a[0]] || 0
      //           const bValue = question.answers[b[0]] || 0
      //           return bValue - aValue
      //         })
      //         for (const a of rawData) {
      //           if (dataLength > 10) {
      //             data[10] = ['Z', data[10][1] + (question.answers[a[0]] || 0)]
      //           } else {
      //             data.push([a[0], question.answers[a[0]] || 0])
      //           }
      //           dataLength++
      //         }
      //       }
      //     }
      //     if (question.questionType === SURVEY_CONSTANT.ITEM_TYPE_RADIO) {
      //       this.pagination.push('pie')
      //     } else {
      //       this.pagination.push('vertical')
      //     }
      //   } else {
      //     data = [...question.answers]
      //     this.pagination.push(1)
      //   }
      //   this.chartData.push(data)
      // }

      // let i = 0
      // for (const q of Object.entries(this.summary.statsPerQuestion)) {
      //   const id = q[0]
      //   const question = q[1]
      //   this.summary.statsPerQuestion[id].questionId = id
      //   if (question.questionType !== SURVEY_CONSTANT.ITEM_TYPE_TEXT) {
      //     if (question.questionType === SURVEY_CONSTANT.ITEM_TYPE_RADIO) {
      //       this.chartOption.push(this.pieChartOption(this.chartData[i], question))
      //     } else {
      //       this.chartOption.push(this.verticalChartOption(this.chartData[i], question))
      //     }
      //   } else {
      //     this.chartOption.push(null)
      //   }
      //   i++
      // }

      if (this.isPdf) {
        const paginations = this.$route.query.chart.split(',')
        const promises = []
        for (let i in this.questionList) {
          const question = this.questionList[i]
          if (question.questionType !== SURVEY_CONSTANT.ITEM_TYPE_TEXT) {
            this.changePagination(i, paginations[i])
          }

          if (question.questionType === SURVEY_CONSTANT.ITEM_TYPE_TEXT) {
            promises.push(new Promise(resolve => {
              getTextAnswer({
                sid: this.$route.query.sid,
                surveyId: this.survey.surveyId,
                start: this.startDate,
                end: this.endDate,
                pageId: 0,
                pageNum: SURVEY_MAX_TEXT_ANSWER_COUNT,
                questionId: Number(question.questionId)
              }).then(result => {
                this.$set(this.chartData, i, result)
                resolve()
              })
            }))
          }
        }
        // for (const q of Object.entries(this.summary.statsPerQuestion)) {
        //   const question = q[1]
        //   if (question.questionType !== SURVEY_CONSTANT.ITEM_TYPE_TEXT) {
        //     this.changePagination(q[0], qi, paginations[qi])
        //   }
        //
        //   if (question.questionType === SURVEY_CONSTANT.ITEM_TYPE_TEXT) {
        //     promises.push(new Promise(resolve => {
        //       getTextAnswer({
        //         sid: this.$route.query.sid,
        //         surveyId: this.survey.surveyId,
        //         start: this.startDate,
        //         end: this.endDate,
        //         pageId: 0,
        //         pageNum: SURVEY_MAX_TEXT_ANSWER_COUNT,
        //         questionId: Number(question.questionId) || q[0]
        //       }).then(result => {
        //         this.$set(this.chartData, qi, result)
        //         resolve()
        //       })
        //     }))
        //   }
        //
        //   qi++
        // }

        for (let i = 0; i < this.chartData.length; i++) {
          const question = this.questionList[i]
          if (question) {
            if (question.questionType === SURVEY_CONSTANT.ITEM_TYPE_TEXT) {
              promises.push(new Promise(resolve => {
                getTextAnswer({
                  sid: this.$route.query.sid,
                  surveyId: this.survey.surveyId,
                  start: this.startDate,
                  end: this.endDate,
                  pageId: 0,
                  pageNum: SURVEY_MAX_TEXT_ANSWER_COUNT,
                  questionId: Number(question.questionId) || i + 1
                }).then(result => {
                  this.$set(this.chartData, i, result)
                  resolve()
                })
              }))
            }
          }
        }

        Promise.all(promises).then(() => {
          setTimeout(() => {
            window.print()
          }, 1000)
        })
      }

      this.loading = false
      this.$emit('loaded', true)
    },
    changePagination (i, value) {
      const question = this.questionList[i]
      if (question.questionType === SURVEY_CONSTANT.ITEM_TYPE_TEXT) {
        getTextAnswer({
          sid: this.$route.query.sid,
          surveyId: this.survey.surveyId,
          start: this.startDate,
          end: this.endDate,
          pageId: value - 1,
          pageNum: 10,
          questionId: Number(question.questionId)
        }).then(result => {
          this.$set(this.chartData, i, result)
        })
      } else {
        if (value === 'pie') {
          this.$set(this.chartOption, i, this.pieChartOption(this.chartData[i], question))
        } else if (value === 'vertical') {
          this.$set(this.chartOption, i, this.verticalChartOption(this.chartData[i], question))
        } else if (value === 'horizontal') {
          this.$set(this.chartOption, i, this.horizontalChartOption(this.chartData[i], question))
        }
      }

      this.$set(this.pagination, i, value)
    },
    pieChartOption (chartData, question) {
      let data = [...chartData]
      data = data.map(el => { return { name: el[0] === 'Z' ? this.$t('common.etc2') : (question.answerOptions[el[0]] || this.$t('userResearch.survey.create.other')), value: el[1] } })

      return {
        series: [
          {
            data,
            type: 'pie',
            color: this.pieColor,
            label: {
              show: false
            },
            labelLine: {
              show: false
            },
            radius: [55, '75%'],
            top: -15,
            left: 30,
            width: 400,
            height: 400,
            animation: !this.$route.query.pdf
          }
        ],
        tooltip: {
          trigger: 'item',
          formatter: (params) => {
            return `<span class="tooltip-value">${params.data.name} : <span>${params.data.value}</span></span>`
          }
        },
        xAxis: {
          axisLine: {
            show: false
          }
        }
      }
    },
    verticalChartOption (chartData, question) {
      let data = [...chartData]
      data.sort((a, b) => {
        if (a[0] === 'Z') return 1
        if (b[0] === 'Z') return -1
        return a[0] - b[0]
      })
      if (question.questionType === SURVEY_CONSTANT.ITEM_TYPE_RANGE) {
        if (question.answerOptions.minValue === 0) {
          data = data.map(el => [question.answerOptions.minValue + Number(el[0]), el[1]])
        } else {
          data = data.map(el => [question.answerOptions.minValue + Number(el[0]) - 1, el[1]])
        }
      } else {
        data = data.map(el => [el[0] === 'Z' ? this.$t('common.etc2') : (question.answerOptions[el[0]] || this.$t('userResearch.survey.create.other')), el[1]])
      }

      let barColor = '#ec0047'
      let barWidth = 16

      if (question.questionType === SURVEY_CONSTANT.ITEM_TYPE_RANGE) {
        if (data.length <= 5) {
          barColor = ['#3265d0', '#7e98ce', '#cccccc', '#dc678a', '#ec0047']
        } else if (data.length <= 6) {
          barColor = ['#3265d0', '#7e98ce', '#b8bfcd', '#d0b2bb', '#dc678a', '#ec0047']
        } else if (data.length <= 7) {
          barColor = ['#3265d0', '#4270d0', '#7e98ce', '#cccccc', '#dc678a', '#e81a58', '#ec0047']
        } else if (data.length <= 8) {
          barColor = ['#3265d0', '#4270d0', '#7e98ce', '#b8bfcd', '#d0b2bb', '#dc678a', '#e81a58', '#ec0047']
        } else if (data.length <= 9) {
          barColor = ['#3265d0', '#4270d0', '#7e98ce', '#b8bfcd', '#cccccc', '#d0b2bb', '#dc678a', '#e81a58', '#ec0047']
        } else if (data.length <= 10) {
          barColor = ['#3265d0', '#4270d0', '#7e98ce', '#b8bfcd', '#cccccc', '#cccccc', '#d0b2bb', '#dc678a', '#e81a58', '#ec0047']
        } else if (data.length <= 11) {
          barColor = ['#3265d0', '#4270d0', '#7e98ce', '#b8bfcd', '#cccccc', '#cccccc', '#cccccc', '#d0b2bb', '#dc678a', '#e81a58', '#ec0047']
        }
      }

      const maxWidth = 850 / chartData.length

      return {
        series: [
          {
            data,
            type: 'bar',
            itemStyle: {
              color: param => {
                if (question.questionType === SURVEY_CONSTANT.ITEM_TYPE_RANGE) {
                  return barColor[param.dataIndex]
                } else {
                  return barColor
                }
              }
            },
            barWidth: barWidth,
            hoverAnimation: false,
            smooth: true,
            zoom: 1,
            zoomLock: true,
            animation: !this.$route.query.pdf
          }
        ],
        legend: {
          show: false
        },
        formatter: (params) => {
          const item1 = params[0]
          return `<span class="tooltip-value">${item1.axisValue} : <span>${item1.value[1]}</span></span>`
        },
        shadowStyle: {
          color: 'rgba(0, 0, 0, 0.12)'
        },
        xAxis: {
          type: 'category',
          boundaryGap: true,
          silent: false,
          splitArea: {
            show: false
          },
          splitLine: {
            show: false
          },
          axisLine: {
            show: true,
            color: '#888888'
          },
          axisTick: {
            show: true,
            alignWithLabel: true,
            lineStyle: {
              width: 1,
              height: 6,
              color: '#888888'
            }
          },
          axisLabel: {
            interval: 0,
            height: 20,
            margin: 19,
            color: '#666666',
            fontSize: 12,
            align: 'center',
            width: maxWidth,
            overflow: 'truncate'
          },
          axisPointer: {
            type: 'shadow'
          }
        },
        yAxis: {
          type: 'value',
          color: '#666666',
          axisLine: {
            show: false
          },
          splitLine: {
            lineStyle: {
              color: '#e6e6e6'
            }
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            color: '#666666'
          },
          min: 0,
          minInterval: 1
        },
        grid: {
          top: 22,
          right: 10,
          bottom: (question.questionType === SURVEY_CONSTANT.ITEM_TYPE_RANGE && (question.answerOptions.minText || question.answerOptions.maxText))
            ? 70 : 45,
          left: 23,
          containLabel: true
        }
      }
    },
    horizontalChartOption (chartData, question) {
      let data = [...chartData]
      data.sort((a, b) => {
        if (a[0] === 'Z') return 1
        if (b[0] === 'Z') return -1
        return a[0] - b[0]
      })
      if (question.questionType === SURVEY_CONSTANT.ITEM_TYPE_RANGE) {
        data = data.map(el => [el[1], question.answerOptions.minValue + Number(el[0]) - 1])
      } else {
        data = data.map(el => [el[1], el[0] === 'Z' ? this.$t('common.etc2') : (question.answerOptions[el[0]] || this.$t('userResearch.survey.create.other'))])
        data.reverse()
      }

      let barColor = '#ec0047'
      let barWidth = 16

      if (question.questionType === SURVEY_CONSTANT.ITEM_TYPE_RANGE) {
        if (data.length <= 5) {
          barColor = ['#3265d0', '#7e98ce', '#cccccc', '#dc678a', '#ec0047']
        } else if (data.length <= 6) {
          barColor = ['#3265d0', '#7e98ce', '#b8bfcd', '#d0b2bb', '#dc678a', '#ec0047']
        } else if (data.length <= 7) {
          barColor = ['#3265d0', '#4270d0', '#7e98ce', '#cccccc', '#dc678a', '#e81a58', '#ec0047']
        } else if (data.length <= 8) {
          barColor = ['#3265d0', '#4270d0', '#7e98ce', '#b8bfcd', '#d0b2bb', '#dc678a', '#e81a58', '#ec0047']
        } else if (data.length <= 9) {
          barColor = ['#3265d0', '#4270d0', '#7e98ce', '#b8bfcd', '#cccccc', '#d0b2bb', '#dc678a', '#e81a58', '#ec0047']
        } else if (data.length <= 10) {
          barColor = ['#3265d0', '#4270d0', '#7e98ce', '#b8bfcd', '#cccccc', '#cccccc', '#d0b2bb', '#dc678a', '#e81a58', '#ec0047']
        } else if (data.length <= 11) {
          barColor = ['#3265d0', '#4270d0', '#7e98ce', '#b8bfcd', '#cccccc', '#cccccc', '#cccccc', '#d0b2bb', '#dc678a', '#e81a58', '#ec0047']
        }
      }

      return {
        series: [
          {
            data,
            type: 'bar',
            itemStyle: {
              color: param => {
                if (question.questionType === SURVEY_CONSTANT.ITEM_TYPE_RANGE) {
                  return barColor[param.dataIndex]
                } else {
                  return barColor
                }
              }
            },
            barWidth: barWidth,
            hoverAnimation: false,
            smooth: true,
            zoom: 1,
            zoomLock: true,
            animation: !this.$route.query.pdf
          }
        ],
        legend: {
          show: false
        },
        formatter: (params) => {
          const item1 = params[0]
          return `<span class="tooltip-value">${item1.axisValue} : <span>${item1.value[0]}</span></span>`
        },
        xAxis: {
          type: 'value',
          color: '#666666',
          axisLine: {
            show: false
          },
          splitLine: {
            show: true,
            lineStyle: {
              color: '#e6e6e6'
            }
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            color: '#666666'
          },
          min: 0,
          minInterval: 1
        },
        yAxis: {
          offset: 0,
          type: 'category',
          boundaryGap: true,
          silent: false,
          splitArea: {
            show: false
          },
          splitLine: {
            show: false
          },
          axisLine: {
            show: false
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            interval: 0,
            height: 40,
            margin: 19,
            color: '#666666',
            fontSize: 12,
            align: 'right',
            width: 100,
            overflow: 'truncate'
          },
          axisPointer: {
            type: 'shadow'
          }
        },
        grid: {
          top: 0,
          right: 20,
          bottom: 55,
          left: (question.questionType === SURVEY_CONSTANT.ITEM_TYPE_RANGE && (question.answerOptions.minText || question.answerOptions.maxText))
            ? 125 : 23,
          containLabel: true
        }
      }
    },
    toggleOtherPages () {
      if (this.otherPage) {
        this.otherPage = false
      } else {
        getUrlEtcRank({
          sid: this.$route.query.sid,
          surveyId: this.survey.surveyId,
          start: this.startDate,
          end: this.endDate
        }).then(result => {
          const resultList = { ...result }
          if (resultList.hasOwnProperty('cache')) {
            delete resultList.cache
          }
          let list = [...Object.values(resultList)]
          list.sort((a, b) => {
            if (a.completeCount > b.completeCount) {
              return -1
            } else if (a.completeCount < b.completeCount) {
              return 1
            }
            return a.title.localeCompare(b.title)
          })
          this.otherPage = list.filter(item => { return item.completeCount > 0 })
        })
      }
    },
    toggleOtherOptions (question) {
      if (this.otherOptions) {
        this.otherOptions = false
      } else {
        const questions = {}
        for (const question of this.survey.questions) {
          questions[question.questionId] = question
        }
        let data = []
        let dataLength = 0
        let rawData = Object.entries(questions[Number(question.questionId)].answerOptions)
        rawData.sort((a, b) => {
          const aValue = question.answers[a[0]] || 0
          const bValue = question.answers[b[0]] || 0
          return bValue - aValue
        })
        for (const a of rawData) {
          if (dataLength >= 10) {
            data.push({
              rank: dataLength,
              option: a[1],
              response: question.answers[a[0]] || 0,
              rate: (question.answers[a[0]] || 0) / question.totalAnswered * 100
            })
          }
          dataLength++
        }
        this.otherOptions = data
      }
    }
  },
  watch: {
    summary () {
      this.makeData()
    },
    chartString () {
      this.$emit('chartString', this.chartString)
    }
  },
  computed: {
    computedPageRankMap () {
      let list = [...Object.values(this.summary.pageRankMap)]
      return list
    },
    chartString () {
      return this.pagination.join(',')
    },
    periodChartOption () {
      if (!this.summary || !this.summary.countMap) {
        return null
      }

      let maxValue = 0
      let minDate = null
      let maxDate = null

      const barData = []
      const lineData = []

      const countMap = [...Object.entries(this.summary.countMap)].sort((a, b) => {
        return a[0].localeCompare(b[0])
      })

      for (const [date, value] of countMap) {
        barData.push([date, value.answered])
        lineData.push([date, value.exposed ? value.answered / value.exposed * 100 : 0])
        maxValue = Math.max(maxValue, value.answered)
        if (!minDate) {
          minDate = date
        }
        maxDate = date
      }

      if (maxValue < 100) {
        maxValue = Math.ceil(maxValue / 10) * 10
      } else if (maxValue < 1000) {
        maxValue = Math.ceil(maxValue / 100) * 100
      } else if (maxValue < 10000) {
        maxValue = Math.ceil(maxValue / 1000) * 1000
      } else if (maxValue < 100000) {
        maxValue = Math.ceil(maxValue / 10000) * 10000
      } else if (maxValue < 1000000) {
        maxValue = Math.ceil(maxValue / 100000) * 100000
      }

      let barWidth = 16

      const options = {
        series: [
          {
            type: 'bar',
            data: barData,
            name: this.$t('userResearch.survey.response.responses'),
            hoverAnimation: false,
            smooth: true,
            color: ['#ec0047'],
            barWidth: barWidth,
            zoom: 1,
            zoomLock: true,
            animation: !this.$route.query.pdf
          },
          {
            type: 'line',
            data: lineData,
            name: this.$t('userResearch.survey.response.responseRate'),
            showSymbol: false,
            hoverAnimation: true,
            smooth: true,
            lineStyle: {
              width: 2
            },
            itemStyle: {
              borderWidth: 2,
              borderColor: '#ea9900'
            },
            symbolSize: 4,
            yAxisIndex: 1,
            color: ['#ea9900'],
            zoom: 1,
            zoomLock: true,
            animation: !this.$route.query.pdf
          }
        ],
        legend: {
          show: false
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          formatter: (params) => {
            const item1 = params[0]
            const item2 = params[1]
            const title = format(parse(item1.axisValue), 'YY-MM-DD')
            return `<span class="tooltip-title">${title}</span>
              <span class="tooltip-value"><em style="background-color: #ec0047"></em>${item1.seriesName} : <span>${item1.value[1]}</span></span>
              <span class="tooltip-value"><em style="background-color: #ea9900"></em>${item2.seriesName} : <span>${item2.value[1].toFixed(1)}%</span></span>`
          },
          shadowStyle: {
            color: 'rgba(0, 0, 0, 0.12)'
          }
        },
        xAxis: {
          type: 'category',
          boundaryGap: true,
          silent: false,
          splitArea: {
            show: false
          },
          splitLine: {
            show: false
          },
          axisLine: {
            show: true,
            color: '#888888'
          },
          axisTick: {
            alignWithLabel: true,
            lineStyle: {
              width: 1,
              height: 6,
              color: '#888888'
            }
          },
          axisLabel: {
            height: 20,
            margin: 11,
            formatter: (value, index) => {
              value = parse(value)
              if (value < minDate || value > maxDate) {
                return false
              }
              return format(value, 'YY-MM-DD')
            },
            color: '#666666',
            fontSize: 12,
            align: 'center'
          },
          axisPointer: {
            type: 'shadow'
          }
        },
        yAxis: [
          {
            type: 'value',
            color: '#666666',
            axisLine: {
              show: false
            },
            splitLine: {
              lineStyle: {
                color: '#e6e6e6'
              }
            },
            axisTick: {
              show: false
            },
            axisLabel: {
              color: '#666666'
            },
            min: 0,
            minInterval: 1,
            max: maxValue
          },
          {
            type: 'value',
            color: '#666666',
            axisLabel: {
              formatter: '{value} %'
            },
            axisLine: {
              show: false
            },
            splitLine: {
              show: false
            },
            axisTick: {
              show: false
            },
            min: 0,
            minInterval: 1
          }
        ],
        grid: {
          top: 12,
          right: 49,
          bottom: 40,
          left: 51,
          containLabel: false
        }
      }

      return options
    },
    hasOtherPage () {
      const totalCount = this.summary.totalAnswered
      const top5Count = Object.values(this.summary.pageRankMap).reduce((prev, curr) => prev + curr.completeCount, 0)
      return totalCount - top5Count
    }
  },
  props: [
    'survey',
    'summary',
    'startDate',
    'endDate',
    'isPdf'
  ],
  data () {
    return {
      SURVEY_CONSTANT: SURVEY_CONSTANT,
      loading: true,
      pagination: [],
      chartData: [],
      chartOption: [],
      pieColor: ['#5ea7ff', '#8e58e3', '#5f2a95', '#c464b6', '#ffaebe', '#ca3d58', '#dbc152', '#529117', '#9ece64', '#454545', '#d7d7d7'],
      otherPage: false,
      otherOptions: false,
      questionList: []
    }
  }
}
</script>

<style lang="scss" scoped>
.survey-response-summary {
  margin-top: 68px;

  .summary-item {
    margin: 60px 0;

    &.questions {
      page-break-before: always;
    }

    h2 {
      font-size: 16px;
      font-weight: 500;
      color: #ec0047;
      padding-left: 10px;

      span {
        display: inline-block;
        vertical-align: middle;
        font-size: 12px;
        font-weight: normal;
        color: #888;
        margin: -2px 0 0 14px;
      }
    }

    .item {
      border-radius: 14px;
      box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.08);
      background-color: #fff;
      padding: 25px 30px;
      margin: 13px 0 20px;

      .counts {
        padding: 25px 25px;

        .value {
          display: inline-block;
          text-align: center;
          margin: 5px 30px -20px;

          .number {
            font-size: 45px;
            font-weight: 500;
            color: #444;

            &.red {
              color: #ec0047;
            }

            &.yellow {
              color: #ea9900;
            }
          }

          .label {
            margin-top: 5px;
            font-size: 13px;
            font-weight: 500;
            color: #444;
          }
        }
      }

      .period-chart {
        & /deep/ {
          .tooltip-title {
            display: block;
            font-weight: 500;
            color: #444;
            font-size: 13px;
            margin: 5px;
          }

          .tooltip-value {
            display: block;
            font-size: 12px;
            margin: 5px;

            em {
              display: inline-block;
              vertical-align: middle;
              width: 8px;
              height: 8px;
              border-radius: 4px;
              margin-right: 5px;
            }

            span {
              float: right;
              font-size: 13px;
              font-weight: 500;
              color: #444;
              margin-left: 5px;
            }
          }
        }
      }

      table {
        width: 100%;
        margin-top: 12px;

        thead {
          th {
            border-bottom: 1px solid #d7d7d7;
            white-space: nowrap;
            font-size: 12px;
            font-weight: 500;
            text-align: center;
            color: #767676;
            padding: 8px 0;

            &.response {
              padding-right: 14px;
              text-align: right;
            }
          }
        }

        tbody {
          td {
            border-bottom: 1px solid #d7d7d7;
            padding: 14px 5px;
            font-size: 14px;
            line-height: 1.57;
            text-align: center;
            color: #555555;
            transition: background-color 0.15s ease;

            button.more {
              width: 34px;
              height: 34px;
              margin: -13px 0 -13px 5px;
              padding: 8px 10px 10px 8px;
              border-radius: 20px;
              border: solid 1px #bbb;
              background-color: #fff;
              transition: background-color 0.15s ease;
              vertical-align: middle;

              &:hover {
                background-color: #f2f2f2;
              }
            }

            &.ranking {
              width: 75px;
            }

            &.response {
              width: 130px;
            }

            &.response {
              width: 130px;
              padding-right: 14px;
              text-align: right;
            }

            &.range {
              text-align: left;
            }

            &.other {
              padding: 6px 0;

              button {
                width: 34px;
                height: 34px;
                padding: 8px 10px 10px 8px;
                border-radius: 20px;
                border: solid 1px #bbb;
                background-color: #fff;
                transition: background-color 0.15s ease;

                &:hover {
                  background-color: #f2f2f2;
                }
              }
            }
          }

          tr:hover {
            td {
              background-color: #f2f2f2;
            }
          }
        }

        tfoot {
          td {
            border-bottom: 1px solid #d7d7d7;
            padding: 14px 0;
            font-size: 14px;
            line-height: 1.57;
            text-align: center;
            color: #555555;
            background: #eeeeee;

            &.response {
              padding-right: 14px;
              text-align: right;
            }

            &.range {
              padding-left: 30px;
              text-align: left;
            }
          }
        }
      }

      &.question {
        page-break-after: always;

        .index {
          font-size: 22px;
          font-weight: 500;
          color: #2f2f2f;
          margin: 17px 0 22px;
        }

        h4 {
          font-size: 18px;
          font-weight: 500;
          line-height: 1.28;
          color: #2f2f2f;
        }

        p.description {
          font-size: 12px;
          line-height: 1;
          color: #0e90a5;
          margin: 6px 0;
        }

        .count {
          font-size: 14px;
          font-weight: 500;
          color: #666;
          margin-top: 20px;

          .number {
            font-size: 16px;
            color: #ec0047;
            margin-left: 5px;
          }
        }

        .chart-type {
          text-align: center;
          margin: 20px 0;

          .ba-button {
            margin: 0 2px 0 3px;

            & /deep/ {
              button {
                width: 32px;
                height: 32px;
              }

              .icon {
                width: 24px;
                height: 24px;
                top: 4px;
                left: 4px;
              }
            }
          }
        }

        .chart-area {
          position: relative;

          .label {
            .min {
              position: absolute;
              bottom: 89px;
              left: 17px;
              color: #3265d0;
              font-size: 12px;
              font-weight: 500;
              line-height: 1.33;
              width: 95px;
            }

            .max {
              position: absolute;
              top: 15px;
              left: 17px;
              color: #ec0047;
              font-size: 12px;
              font-weight: 500;
              line-height: 1.33;
              width: 95px;
            }

            &.vertical {
              .min {
                top: auto;
                bottom: 42px;
                left: 50px;
                width: auto;
              }

              .max {
                top: auto;
                bottom: 42px;
                left: auto;
                right: 10px;
                width: auto;
              }
            }
          }

          .legend {
            position: absolute;
            top: 50%;
            left: 530px;
            transform: translateY(calc(-50% - 15px));

            li {
              width: 300px;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              margin: 10px 0;
              font-size: 14px;
              line-height: 1.57;
              color: #2f2f2f;

              svg {
                vertical-align: middle;
                margin-right: 8px;
              }
            }
          }
        }

        .chart-data {
          margin-top: -10px;
        }

        .question-answers {
          .navigation {
            text-align: center;
            margin: 10px 0;

            &.bottom {
              margin: 3px 0 0;
            }
          }

          ul {
            max-height: 1000px;
            overflow-y: auto;
            border-top: 1px solid #d7d7d7;
            border-bottom: 1px solid #d7d7d7;

            &.pdf {
              max-height: none;
            }

            li {
              border-top: 1px solid #d7d7d7;
              font-size: 14px;
              line-height: 1.57;
              color: #2f2f2f;
              padding: 15px 0;

              &:first-child {
                border-top: none;
              }
            }
          }
        }
      }
    }
  }

  .other-pages {
    position: relative;
    width: 830px;
    height: 600px;
    border-radius: 14px;
    box-shadow: 0 8px 15px 0 rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.16);
    background-color: #fff;

    h5 {
      padding: 30px;
      font-size: 15px;
      font-weight: 500;
      color: #2f2f2f;
    }

    .icon--close-size16 {
      position: absolute;
      top: 12px;
      right: 12px;
    }

    .thead {
      padding: 0 30px;

      table {
        width: 100%;

        th {
          border-bottom: 1px solid #bbb;
          font-size: 13px;
          font-weight: 500;
          line-height: 1.23;
          text-align: center;
          color: #767676;
          padding: 13px 10px 10px;

          &.name {
            text-align: left;
            padding-left: 150px;
          }

          &.rank {
            width: 50px;
          }

          &.count {
            width: 50px;
          }

          &.rate {
            width: 160px;
          }
        }
      }
    }

    .tbody {
      height: 450px;
      padding: 0 30px;
      overflow: auto;

      table {
        width: 100%;

        td {
          border-bottom: 1px solid #ebebeb;
          font-size: 13px;
          line-height: 1;
          color: #555;
          padding: 17px 10px 16px;

          &.name {
            padding-left: 150px;
          }

          &.rank {
            text-align: center;
            width: 50px;
          }

          &.count {
            text-align: right;
            width: 50px;
          }

          &.rate {
            text-align: right;
            width: 160px;

            .bar {
              float: left;
              display: inline-block;
              vertical-align: middle;
              width: 100px;
              height: 10px;
              margin: 2px 0 0 15px;
              border-radius: 5px;
              background-color: #ebebeb;
              overflow: hidden;
              text-align: left;

              span {
                display: inline-block;
                height: 11px;
                background-color: #ec0047;
              }
            }
          }

          a {
            color: inherit;

            .icon {
              vertical-align: middle;
              margin-right: 5px;
            }
          }
        }
      }
    }
  }
}
</style>
